import React, { useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Header from './header/main';
import Sidebar from './sidebar/main';
import { SET_USER_AUTHENTICATED } from '../redux/actionTypes';
import { activateDemo, deactivateDemo, initializeApp } from '../redux/actions';
import store from '../../store';
import { headerHeight } from '../../constants/dimensions';
import Button from '@material-ui/core/Button';
import { secondaryMain, primaryMedium } from '../../constants/colors';
import { useLocalStorage } from '../hooks/UseLocalStorage';
import InfoModal from '../data_display/InfoModal';
import GuideInfoModal from '../data_display/GuideInfoModal';
import { useState } from 'react';
import { useAppAuth } from '../../hooks/useAppAuth';
import TrialSignupModal from '../../components/TrialSignupModal';

const useStyles = makeStyles(theme => ({
  main: {
    paddingTop: headerHeight + 24,
    paddingBottom: 12,
    paddingLeft: 96,
    paddingRight: 24,
  },
  infoBox: {
    width: 480,
    background: `linear-gradient(45deg, ${secondaryMain} 0%, ${primaryMedium} 100%)`,
    margin: '0 auto',
    marginTop: -12,
    marginBottom: 12,
    color: 'white',
    fontSize: 16,
    height: 32,
    padding: 0,
    lineHeight: '32px',
    display: 'block',

    '&:hover': {
      background: `linear-gradient(45deg, ${primaryMedium} 0%, ${secondaryMain} 100%)`,
      fontWeight: 'bold',
    },
  },
  modalButtonContainer: {
    flex: '0 0 100%',
    marginBottom: -12,
  },
  closeModalButton: {
    width: 200,
  },
  trialSignupButton: {
    marginTop: 16,
    marginBottom: 8,
    backgroundColor: '#415578',
    color: 'white',
    '&:hover': {
      backgroundColor: '#0b111b',
    },
  },
}));


function Layout(props) {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { children, isAuthenticated, demoEnabled, showTrialButton, company, company_expired, no_companies, isNewSignup } = props;

  const [hasSeenDemoModal, setHasSeenDemoModal] = useState(sessionStorage.getItem('hasSeenDemoModal') === 'true');
  const [trialSignupModalOpen, setTrialSignupModalOpen] = useState(false);

  React.useEffect(() => {
    const startsWithDemo = location.pathname.startsWith('/demo');
    if (startsWithDemo && !hasSeenDemoModal) {
      setModalOpen(true);
    }
  }, [location, hasSeenDemoModal]);

  // Auto-open trial modal for new signups without companies
  React.useEffect(() => {
    if (isNewSignup && isAuthenticated && !demoEnabled && no_companies) {
      // Check if user has already seen the trial modal (in case of refresh)
      const hasSeenTrialModal = sessionStorage.getItem('hasSeenNewSignupTrialModal') === 'true';

      if (!hasSeenTrialModal) {
        // Only open if they don't have any companies
        setTrialSignupModalOpen(true);
        sessionStorage.setItem('hasSeenNewSignupTrialModal', 'true');
      }
    }
  }, [isNewSignup, isAuthenticated, demoEnabled, no_companies]);

  const [hasClearedAccountModal, setHasClearedAccountModal] = useState(false);

  React.useEffect(() => {
    const startsWithDemo = location.pathname.startsWith('/demo');
    store.dispatch(startsWithDemo ? activateDemo() : deactivateDemo());
  }, [location]);

  // Use the new auth hook instead of AWS Amplify
  // The hook handles authentication, demo mode detection, and token management
  const auth = useAppAuth();

  React.useEffect(() => {
    if (isAuthenticated || demoEnabled) {
      store.dispatch(initializeApp());
    }
  }, [isAuthenticated, demoEnabled]);

  const [modalOpen, setModalOpen] = React.useState(false);
  const handleModalClose = () => {
    sessionStorage.setItem('hasSeenDemoModal', 'true');
    setHasSeenDemoModal(true);
    setModalOpen(false);
  }

  React.useEffect(() => {
    const startsWithDemo = location.pathname.startsWith('/demo');
    if (startsWithDemo && !hasSeenDemoModal) {
      setModalOpen(true);
    }
  }, [location, hasSeenDemoModal]);

  const [accountModalOpen, setAccountModalOpen] = React.useState(false);
  const handleAccountModalClose = () => {
    setHasClearedAccountModal(true);
    setAccountModalOpen(false);
  }

  React.useEffect(() => {
    // Don't show account modal if we're showing the trial signup modal for new users
    // For new signups with no companies, the trial modal will handle it
    if (isNewSignup && no_companies && !company_expired) {
      setAccountModalOpen(false);
    } else {
      // Show account modal for:
      // - Expired companies (regardless of new signup status)
      // - No companies for existing users (not new signups)
      setAccountModalOpen(company_expired || (no_companies && !isNewSignup));
    }
  }, [company_expired, no_companies, isNewSignup]);

  React.useEffect(() => {
    setHasClearedAccountModal(false);
  }, [company, company_expired, no_companies]);

  const closeModalProps = {
    color: "secondary",
    variant: "contained",
    type: "submit",
    className: classes.closeModalButton,
    onClick: handleModalClose
  };

  useEffect(() => {
    if (hasSeenDemoModal && location.hash === '#reset-has-seen-demo') {
      setHasSeenDemoModal(false);
      history.push({
        ...location,
        hash: '',
      })
    }
  }, [hasSeenDemoModal, setHasSeenDemoModal, location, history]);

  // Handle successful trial creation
  const handleTrialSuccess = (data) => {
    console.log('Trial created successfully:', data);

    // Close the trial signup modal
    setTrialSignupModalOpen(false);

    // Close the "no companies" modal
    setAccountModalOpen(false);

    // Refresh the app to load the new company data
    store.dispatch(initializeApp());

    // Redirect to the release configuration page
    history.push('/config/release');
  };

  return (
    <React.Fragment>
      <CssBaseline />
      <Header isAuthenticated={isAuthenticated} demoEnabled={demoEnabled} />

      { (isAuthenticated || demoEnabled) && <Sidebar /> }
    
      <main className={classes.main}>
        { showTrialButton && (
          <div style={{width: '100%'}}>
            <Button
              variant='contained'
              className={classes.infoBox}
              onClick={() => {
                if (demoEnabled) {
                  // In demo mode, redirect to Cognito signup
                  const signupUrl = `https://${process.env.REACT_APP_COGNITO_OAUTH_DOMAIN}/signup?client_id=${process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(process.env.REACT_APP_COGNITO_REDIRECT_URI)}`;
                  window.location = signupUrl;
                } else {
                  // Authenticated users can use the in-app modal
                  setTrialSignupModalOpen(true);
                }
              }}
            >
              START YOUR FOUR WEEK FREE TRIAL
            </Button>
          </div>
        )}
        { (isAuthenticated || demoEnabled) && children }

        <GuideInfoModal title={"Welcome to our Demo Page!"} open={modalOpen} onClose={handleModalClose} closeModalProps={closeModalProps}>
          <div>
            <p>
              We're thrilled to have you here. Explore our latest version of STAR, the cutting-edge solution that's setting new standards in the industry. We've prepared four sets of project data to demonstrate the power of our Automated Defect Prediction tool across various cases, all powered by 3S. Feel free to test drive our features and read more about our tool from the menu bar.
            </p>
            <p>
              Enjoy your demo! If you have any questions or need assistance, <a href={process.env.REACT_APP_CONTACT_US_URL}>our support team</a> is just a click away.
            </p>
          </div>
        </GuideInfoModal>

        <InfoModal title="No Companies Registered" open={no_companies && accountModalOpen} onClose={handleAccountModalClose}>
          <div>
            <p>
              Looks like you don't have any companies tied to your account.
            </p>
            <Button
              variant="contained"
              className={classes.trialSignupButton}
              onClick={() => setTrialSignupModalOpen(true)}
              fullWidth
            >
              Start Your Free Trial
            </Button>
          </div>
        </InfoModal>

        <InfoModal title="Company Membership Expired" open={company_expired && accountModalOpen} onClose={handleAccountModalClose}>
          <div>
            <p>
              Looks like your company's membership has expired
            </p>
            <p>
              <a href={`${process.env.REACT_APP_LANDING_URL}/contact`}>Contact us</a> to continue using STAR
            </p>
          </div>
        </InfoModal>

        {/* Trial Signup Modal */}
        <TrialSignupModal
          open={trialSignupModalOpen}
          onClose={() => setTrialSignupModalOpen(false)}
          onSuccess={handleTrialSuccess}
        />
      </main>
    </React.Fragment>
  );
}

function mapStateToProps(state) {
  return {
    isAuthenticated: state.common.isAuthenticated,
    demoEnabled: state.common.demoEnabled,
    showTrialButton: state.common.demoEnabled && state.common.company && state.common.company.id === 1,
    company_expired: state.common.company_expired,
    no_companies: state.common.no_companies,
    company: state.common.company,
    isNewSignup: state.common.isNewSignup || false,
  };
} 

export default connect(mapStateToProps)(Layout)